import BlotFormatter, {
    Action,
    AlignAction,
    Aligner,
    Alignment,
    DeleteAction,
    ImageSpec,
    ResizeAction,
} from "quill-blot-formatter";
import ReactQuill, { Quill } from "react-quill-new";

// 커스텀 Aligner - float 대신 margin으로 정렬 (텍스트가 이미지 옆으로 붙지 않도록)
class BlockAligner implements Aligner {
    alignments: Record<string, Alignment>;
    alignAttribute: string;
    applyStyle: boolean;

    constructor() {
        this.alignAttribute = "data-align";
        this.applyStyle = true;
        this.alignments = {
            left: {
                name: "left",
                icon: `<svg viewbox="0 0 18 18"><line class="ql-stroke" x1="3" x2="15" y1="9" y2="9"></line><line class="ql-stroke" x1="3" x2="13" y1="14" y2="14"></line><line class="ql-stroke" x1="3" x2="9" y1="4" y2="4"></line></svg>`,
                apply: (el: HTMLElement) => {
                    this.setAlignment(el, "left");
                    this.setStyle(el, "block", "0 auto 1em 0");
                },
            },
            center: {
                name: "center",
                icon: `<svg viewbox="0 0 18 18"><line class="ql-stroke" x1="15" x2="3" y1="9" y2="9"></line><line class="ql-stroke" x1="14" x2="4" y1="14" y2="14"></line><line class="ql-stroke" x1="12" x2="6" y1="4" y2="4"></line></svg>`,
                apply: (el: HTMLElement) => {
                    this.setAlignment(el, "center");
                    this.setStyle(el, "block", "0 auto 1em auto");
                },
            },
            right: {
                name: "right",
                icon: `<svg viewbox="0 0 18 18"><line class="ql-stroke" x1="15" x2="3" y1="9" y2="9"></line><line class="ql-stroke" x1="15" x2="5" y1="14" y2="14"></line><line class="ql-stroke" x1="15" x2="9" y1="4" y2="4"></line></svg>`,
                apply: (el: HTMLElement) => {
                    this.setAlignment(el, "right");
                    this.setStyle(el, "block", "0 0 1em auto");
                },
            },
        };
    }

    getAlignments(): Alignment[] {
        return Object.values(this.alignments);
    }

    clear(el: HTMLElement): void {
        el.removeAttribute(this.alignAttribute);
        el.style.removeProperty("display");
        el.style.removeProperty("margin");
    }

    isAligned(el: HTMLElement, alignment: Alignment): boolean {
        return el.getAttribute(this.alignAttribute) === alignment.name;
    }

    setAlignment(el: HTMLElement, value: string): void {
        el.setAttribute(this.alignAttribute, value);
    }

    setStyle(el: HTMLElement, display: string, margin: string): void {
        if (this.applyStyle) {
            el.style.setProperty("display", display);
            el.style.setProperty("float", "none");
            el.style.setProperty("margin", margin);
        }
    }
}

// 커스텀 AlignAction
class CustomAlignAction extends AlignAction {
    constructor(formatter: BlotFormatter) {
        super(formatter);
        this.aligner = new BlockAligner();
    }
}

// 링크 추가/수정 Action
class LinkAction extends Action {
    button: HTMLElement | null = null;

    onCreate() {
        const button = document.createElement("button");
        button.type = "button"; // form submit 방지
        button.innerHTML = `<svg viewBox="0 0 18 18" width="18" height="18" style="fill: none; stroke: #444; stroke-width: 2;">
            <line x1="7" x2="11" y1="7" y2="11"></line>
            <path d="M8.9,4.577a3.476,3.476,0,0,1,.36,4.679A3.476,3.476,0,0,1,4.577,8.9C3.185,7.5,2.035,6.4,4.217,4.217S7.5,3.185,8.9,4.577Z"></path>
            <path d="M13.423,9.1a3.476,3.476,0,0,0-4.679-.36,3.476,3.476,0,0,0,.36,4.679c1.392,1.392,2.5,2.542,4.679.36S14.815,10.5,13.423,9.1Z"></path>
        </svg>`;
        button.style.cssText = `
            position: absolute;
            top: -30px;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            cursor: pointer;
            z-index: 100;
        `;
        button.title = "이미지 링크";
        button.addEventListener("click", this.handleClick);
        this.button = button;

        // overlay에 버튼 추가
        this.formatter.overlay.appendChild(button);
    }

    onDestroy() {
        if (this.button) {
            this.button.removeEventListener("click", this.handleClick);
            if (this.button.parentNode) {
                this.button.parentNode.removeChild(this.button);
            }
            this.button = null;
        }
    }

    handleClick = () => {
        const img = this.formatter.currentSpec?.getTargetElement();
        if (!img) return;

        const currentLink = img.getAttribute("data-link") || "";
        const url = prompt("이미지에 연결할 링크 URL을 입력하세요:", currentLink);

        if (url === null) return; // 취소

        if (url.trim() === "") {
            // 링크 제거
            img.removeAttribute("data-link");
            img.style.cursor = "";
        } else {
            // 링크 설정
            img.setAttribute("data-link", url.trim());
            img.style.cursor = "pointer";
        }

        // Quill 에디터에 변경 알림
        const editor = document.querySelector(".ql-editor");
        if (editor) {
            editor.dispatchEvent(new Event("input", { bubbles: true }));
        }
    };
}

// 커스텀 ImageSpec - CustomAlignAction, LinkAction 사용
class CustomImageSpec extends ImageSpec {
    getActions() {
        return [ResizeAction, CustomAlignAction, LinkAction, DeleteAction];
    }
}

// 이미지 style, 크기, 정렬 속성 유지를 위한 커스텀 Image Blot
const BaseImage = Quill.import("formats/image");

interface ImageValue {
    src: string;
    style?: string;
    width?: string;
    height?: string;
    "data-align"?: string;
    "data-link"?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
class StyledImage extends (BaseImage as any) {
    static create(value: string | ImageValue) {
        const node = super.create(value) as HTMLElement;
        if (typeof value === "object" && value !== null) {
            node.setAttribute("src", value.src || "");
            if (value.style) node.setAttribute("style", value.style);
            if (value.width) node.setAttribute("width", value.width);
            if (value.height) node.setAttribute("height", value.height);
            if (value["data-align"]) node.setAttribute("data-align", value["data-align"]);
            if (value["data-link"]) {
                node.setAttribute("data-link", value["data-link"]);
                node.style.cursor = "pointer";
            }
        }
        return node;
    }

    static value(node: HTMLElement): ImageValue {
        return {
            src: node.getAttribute("src") || "",
            style: node.getAttribute("style") || undefined,
            width: node.getAttribute("width") || undefined,
            height: node.getAttribute("height") || undefined,
            "data-align": node.getAttribute("data-align") || undefined,
            "data-link": node.getAttribute("data-link") || undefined,
        };
    }

    static formats(node: HTMLElement): ImageValue {
        return {
            src: node.getAttribute("src") || "",
            style: node.getAttribute("style") || undefined,
            width: node.getAttribute("width") || undefined,
            height: node.getAttribute("height") || undefined,
            "data-align": node.getAttribute("data-align") || undefined,
            "data-link": node.getAttribute("data-link") || undefined,
        };
    }
}
StyledImage.blotName = "image";
StyledImage.tagName = "IMG";

Quill.register(StyledImage, true);
Quill.register("modules/blotFormatter", BlotFormatter);

interface EditorProps {
    value: string;
    onChange?: (content: string) => void;
    placeholder?: string;
    className?: string;
    readOnly?: boolean;
}

// data-link 속성이 있는 이미지를 a 태그로 감싸는 유틸 함수 (저장 시 사용)
export const wrapImageWithLink = (html: string): string => {
    return html.replace(
        /<img([^>]*?)data-link="([^"]*)"([^>]*?)>/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer"><img$1data-link="$2"$3></a>',
    );
};

export default function Editor({ value, onChange, placeholder, className, readOnly }: EditorProps) {
    const blotFormatterOptions = {
        specs: [CustomImageSpec],
    };

    const modules = readOnly
        ? { toolbar: false, blotFormatter: blotFormatterOptions }
        : {
              toolbar: {
                  container: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ align: [] }],
                      [{ color: [] }, { background: [] }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link", "image", "video"],
                      ["clean"],
                  ],
              },
              blotFormatter: blotFormatterOptions,
          };

    return (
        <div id="editor" className={className}>
            <ReactQuill
                value={value}
                onChange={onChange}
                modules={modules}
                className={
                    readOnly
                        ? "[&>.ql-container>.ql-editor]:p-0 [&>.ql-container]:!border-none"
                        : "[&>.ql-container>.ql-editor]:min-h-[200px] [&>.ql-container]:rounded-b-[5px] [&>.ql-container]:!border-[#ddd] [&>.ql-container]:bg-white [&>.ql-toolbar]:rounded-t-[5px] [&>.ql-toolbar]:!border-[#ddd] [&>.ql-toolbar]:bg-white"
                }
                theme="snow"
                bounds="#editor"
                placeholder={placeholder}
                readOnly={readOnly}
            />
        </div>
    );
}
